import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { HttpService } from '../../../services/http.service';
import { ToastService } from '../../../services/toast.service';
import { environment } from '../../../../environments/environment';

interface DialogData {
  filterModel: any;
  sortModel: any;
  selectedColumns: string[];
  tableType: 'stage' | 'production' | 'changes';
  tagFilter?: any;
}

@Component({
  selector: 'app-schedule-export-dialog',
  templateUrl: './schedule-export-dialog.component.html',
  styleUrls: ['./schedule-export-dialog.component.scss']
})
export class ScheduleExportDialogComponent implements OnInit {
  public form: FormGroup;
  public saving = false;

  // Common cron expressions
  public cronPresets = [
    { label: 'Every 15 minutes', value: '*/15 * * * *' },
    { label: 'Every 30 minutes', value: '*/30 * * * *' },
    { label: 'Every hour', value: '0 * * * *' },
    { label: 'Every 2 hours', value: '0 */2 * * *' },
    { label: 'Every 6 hours', value: '0 */6 * * *' },
    { label: 'Daily at midnight', value: '0 0 * * *' },
    { label: 'Daily at 6 AM', value: '0 6 * * *' },
    { label: 'Daily at 9 AM', value: '0 9 * * *' },
    { label: 'Daily at 12 PM', value: '0 12 * * *' },
    { label: 'Daily at 6 PM', value: '0 18 * * *' },
    { label: 'Weekly (Sunday midnight)', value: '0 0 * * 0' },
    { label: 'Weekly (Monday 9 AM)', value: '0 9 * * 1' },
    { label: 'Monthly (1st at midnight)', value: '0 0 1 * *' }
  ];

  public timezones = [
    'America/New_York',
    'America/Chicago',
    'America/Denver',
    'America/Los_Angeles',
    'America/Phoenix',
    'UTC'
  ];

  public exportFormats = [
    { value: 'xlsx', label: 'Excel (.xlsx)' },
    { value: 'csv', label: 'CSV (.csv)' }
  ];

  constructor(
    private fb: FormBuilder,
    private httpService: HttpService,
    private toastService: ToastService,
    public dialogRef: MatDialogRef<ScheduleExportDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DialogData
  ) {
    this.form = this.fb.group({
      name: ['', [Validators.required, Validators.maxLength(100)]],
      description: ['Automated export of filtered products', [Validators.maxLength(500)]],
      cronExpression: ['0 9 * * *', [Validators.required]],
      timezone: ['America/Chicago', Validators.required],
      exportFormat: ['xlsx', Validators.required],
      isActive: [true]
    });
  }

  ngOnInit(): void {
    // Set default name based on table type and timestamp
    const timestamp = new Date().toISOString().split('T')[0];
    const defaultName = `${this.data.tableType.charAt(0).toUpperCase() + this.data.tableType.slice(1)} Export - ${timestamp}`;
    this.form.patchValue({ name: defaultName });
  }

  onPresetSelected(preset: string): void {
    this.form.patchValue({ cronExpression: preset });
  }

  getFilterSummary(): string {
    const filterModel = this.data.filterModel || {};
    const filterCount = Object.keys(filterModel).length;

    if (filterCount === 0) {
      return 'No filters applied';
    }

    const filters = Object.entries(filterModel).map(([field, filter]: [string, any]) => {
      if (filter.filter) {
        return `${field}: ${filter.filter}`;
      } else if (filter.values) {
        return `${field}: ${filter.values.length} values`;
      }
      return `${field}: filtered`;
    });

    return filters.slice(0, 3).join(', ') + (filterCount > 3 ? `, +${filterCount - 3} more` : '');
  }

  getColumnSummary(): string {
    const count = this.data.selectedColumns?.length || 0;
    if (count === 0) return 'No columns selected';
    return `${count} columns selected`;
  }

  onCancel(): void {
    this.dialogRef.close(false);
  }

  onSave(): void {
    if (this.form.invalid) {
      this.toastService.error('Please fill in all required fields');
      return;
    }

    this.saving = true;
    const formValue = this.form.value;

    const scheduledEvent = {
      name: formValue.name,
      description: formValue.description,
      eventType: 'export',
      cronExpression: formValue.cronExpression,
      timezone: formValue.timezone,
      isActive: formValue.isActive,
      configuration: {
        exportType: this.data.tableType,
        exportFormat: formValue.exportFormat,
        filterModel: this.data.filterModel || {},
        sortModel: this.data.sortModel || [],
        selectedFields: this.data.selectedColumns || [],
        tagFilter: this.data.tagFilter || null
      },
      tags: ['auto-export', this.data.tableType]
    };

    this.httpService.post(environment.API_PATH + '/scheduler/events', scheduledEvent)
      .subscribe({
        next: (response) => {
          this.toastService.success('Scheduled export created successfully');
          this.saving = false;
          this.dialogRef.close(true);
        },
        error: (error) => {
          console.error('Error creating scheduled export:', error);
          this.toastService.error('Failed to create scheduled export');
          this.saving = false;
        }
      });
  }

  validateCron(): void {
    const cronValue = this.form.get('cronExpression')?.value;
    if (!cronValue) return;

    this.httpService.post(environment.API_PATH + '/scheduler/validate-cron', { cronExpression: cronValue })
      .subscribe({
        next: (response: any) => {
          if (response.valid) {
            this.toastService.success(`Valid! Next run: ${new Date(response.nextRun).toLocaleString()}`);
          } else {
            this.toastService.error('Invalid cron expression');
          }
        },
        error: (error) => {
          this.toastService.error('Failed to validate cron expression');
        }
      });
  }
}
